<template>
  <div 
    v-if="isVisible" 
    class="toast" 
    :class="[type, { 'slide-in': isVisible }]"
  >
    <div class="toast-icon">
      <span v-if="type === 'success'">✅</span>
      <span v-else-if="type === 'error'">❌</span>
      <span v-else-if="type === 'info'">ℹ️</span>
    </div>
    <div class="toast-content">
      <div class="toast-title">{{ title }}</div>
      <div class="toast-message">{{ message }}</div>
    </div>
    <div class="progress-bar" :style="{ width: progress + '%' }"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, defineProps } from 'vue';

const props = defineProps<{
  title: string;
  message: string;
  duration?: number;
  type?: 'success' | 'error' | 'info';
}>();

const isVisible = ref(false);
const progress = ref(100);
let progressInterval: number;

onMounted(() => {
  isVisible.value = true;
  const duration = props.duration || 5000;
  const updateInterval = 10;
  const steps = duration / updateInterval;
  let currentStep = 0;

  progressInterval = setInterval(() => {
    currentStep++;
    progress.value = 100 - (currentStep / steps * 100);
    
    if (currentStep >= steps) {
      clearInterval(progressInterval);
      isVisible.value = false;
    }
  }, updateInterval);
});

onBeforeUnmount(() => {
  if (progressInterval) {
    clearInterval(progressInterval);
  }
});
</script>

<script lang="ts">
export default {
  name: 'ToastComponent'
};
</script>
<style scoped>
.toast {
  position: relative;
  top: 0;
  right: 0;
  left: 0;
  min-width: auto;
  max-width: 100%;
  background: var(--bg-secondary);
  border-radius: 8px;
  padding: 16px;
  color: var(--text-primary);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 99999;
  display: flex;
  gap: 12px;
  overflow: hidden;
  opacity: 0;
  transform: translateY(-20px);
  pointer-events: auto;
  margin-bottom: 16px;
}

.toast.slide-in {
  animation: slideIn 0.3s forwards;
}

.toast.success {
  border-left: 4px solid var(--bg-success);
}

.toast.error {
  border-left: 4px solid var(--bg-danger);
}

.toast.info {
  border-left: 4px solid var(--bg-info);
}

.toast-icon {
  font-size: 20px;
  display: flex;
  align-items: center;
}

.toast-content {
  flex: 1;
}

.toast-title {
  font-weight: 500;
  margin-bottom: 4px;
}

.toast-message {
  font-size: 14px;
  opacity: 0.8;
}

.progress-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: var(--bg-success);
  transition: width 0.1s linear;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
